import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { formatDate } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { PlacePickerComponent } from "src/app/place-picker/place-picker.component";
import { ApiService } from "src/app/shared/services/api.service";
import { ToastrMsgService } from "src/app/shared/services/shared-services/toastr.service";
import { UnitConversionService } from "src/app/shared/services/unit-conversion.service";
import { Observable, map, startWith } from 'rxjs';

@Component({
  selector: 'app-add-new-project',
  templateUrl: './add-new-project.component.html',
  styleUrl: './add-new-project.component.css'
})
export class AddNewProjectComponent {
  @ViewChildren('fileInputRef') fileInputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  editMode = false;
  projectName = "";
  fileInput: { [key: string]: HTMLInputElement } = {};
  projectForm!: FormGroup;
  locationForm!: FormGroup;
  clientForm!: FormGroup;
  selectedDomain = '';
  selectedDoc = '';
  selectedDom = '';
  domainOptions: any = [];
  
  // FSI options for construction projects (0.5 to 4.0 with 0.5 increments)
  fsiOptions: number[] = [];
  // [
  //   'Residential', 'Commercial', 'Educational', 'Water',
  //   'Power', 'Gas', 'Telecom', 'Road', 'Consultancy'
  // ];

  // Sub-domain mapping based on domain
  subDomainMapping: { [key: string]: string[] } = {
    'construction': ['Residential', 'Commercial', 'Education'],
    'consultancy': ['ESG Consultancy', 'Architects', 'Other'],
    'road': ['National Highway', 'State Highway', 'District Roads', 'Village Road'],
    'utilities': ['Water', 'Gas', 'Electricity']
  };

  // Road services mapping based on road type
  roadServicesMapping: { [key: string]: string[] } = {
    'National Highway': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'State Highway': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'District Roads': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'Village Road': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ]
  };

  /**
   * Suggested default FSI when API has no value — typical planning norms by sub-domain.
   * Only applied on create or when normalizing an empty/invalid stored value.
   */
  readonly defaultFsiBySubDomain: Record<string, number> = {
    Residential: 2,
    Commercial: 2.5,
    Education: 2,
  };

  /** Mat-select compare: API may send numeric strings; options are numbers. */
  compareFsi = (a: number | string | null | undefined, b: number | string | null | undefined): boolean => {
    const na = a === null || a === undefined || a === '' ? NaN : Number(a);
    const nb = b === null || b === undefined || b === '' ? NaN : Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return false;
    return Math.abs(na - nb) < 1e-9;
  };

  /** Coerce API / user value to a valid entry in `fsiOptions`. */
  normalizeFsiForSelect(raw: unknown, subDomainHint?: string | null): number {
    const fallback =
      (subDomainHint && this.defaultFsiBySubDomain[subDomainHint.trim()]) ?? 2;
    if (raw === null || raw === undefined || raw === '') {
      return fallback;
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
    if (Number.isNaN(n) || n <= 0) {
      return fallback;
    }
    const allowed = this.fsiOptions;
    if (allowed.length === 0) {
      return Math.min(4, Math.max(0.5, Math.round(n * 2) / 2));
    }
    if (allowed.some((v) => Math.abs(v - n) < 1e-9)) {
      return allowed.find((v) => Math.abs(v - n) < 1e-9)!;
    }
    const stepped = Math.round(n * 2) / 2;
    const clamped = Math.min(4, Math.max(0.5, stepped));
    if (allowed.some((v) => Math.abs(v - clamped) < 1e-9)) {
      return clamped;
    }
    return allowed.reduce((best, v) =>
      Math.abs(v - n) < Math.abs(best - n) ? v : best
    );
  }

  // Get sub-domains for selected domain
  getSubDomains(): string[] {
    const domain = this.projectForm?.get('domain')?.value?.toLowerCase();
    return this.subDomainMapping[domain] || [];
  }

  // Get road services for selected road type
  getRoadServices(): string[] {
    const roadType = this.projectForm?.get('sub_domain')?.value;
    if (this.projectForm?.get('domain')?.value?.toLowerCase() === 'road' && roadType) {
      return this.roadServicesMapping[roadType] || [];
    }
    return [];
  }

  // Check if road services section should be displayed
  showRoadServices(): boolean {
    return this.projectForm?.get('domain')?.value?.toLowerCase() === 'road' && 
           this.projectForm?.get('sub_domain')?.value;
  }

  /** Hide Size, Unit, FSI and Construction Area Size when domain is Road or Utilities */
  showSizeAndFsiFields(): boolean {
    const domain = (this.projectForm?.get('domain')?.value || '').toLowerCase();
    return domain !== 'road' && domain !== 'utilities';
  }

  domainFields: { [key: string]: any[] } = {
    Residential: [
      { key: 'housing_units', label: 'Housing Units', type: 'number', value: '' },
      { key: 'unit_estimate', label: 'Unit Estimate', type: 'number', value: '' },
      { key: 'unit_layout_changes', label: 'Unit Layout Changes', type: 'text', value: '' },
      { key: 'foundation_progress', label: 'Foundation Progress', type: 'percentage', value: '' },
      { key: 'unit_completion_rate', label: 'Unit Completion Rate', type: 'number', value: '' },
      { key: 'fire_safety_compliance', label: 'Fire Safety Compliance', type: 'text', value: '' },
      { key: 'occupancy_certificate', label: 'Occupancy Certificate', type: 'text', value: '' },
      { key: 'handover_date', label: 'Handover Date', type: 'date', value: '' },
      { key: 'resident_onboarding_docs', label: 'Resident Onboarding Docs', type: 'text', value: '' },
      { key: 'tile_type_issued', label: 'Tile Type Issued', type: 'text', value: '' },
      { key: 'paint_quantity', label: 'Paint Quantity', type: 'volume', value: '' },
      { key: 'tile_pallets_received', label: 'Tile Pallets Received', type: 'number', value: '' },
      { key: 'paint_drums', label: 'Paint Drums', type: 'volume', value: '' },
    ],
    Commercial: [
      { key: 'retail_space', label: 'Retail Space', type: 'size', value: '' },
      { key: 'market_demand_analysis', label: 'Market Demand Analysis', type: 'text', value: '' },
      { key: 'storefront_design', label: 'Storefront Design', type: 'text', value: '' },
      { key: 'store_construction', label: 'Store Construction', type: 'text', value: '' },
      { key: 'hvac_performance_logs', label: 'HVAC Performance Logs', type: 'text', value: '' },
      { key: 'elevator_downtime', label: 'Elevator Downtime', type: 'duration', value: '' },
      { key: 'lease_agreement_finalized', label: 'Lease Agreement Finalized', type: 'text', value: '' },
      { key: 'facility_management_plan', label: 'Facility Management Plan', type: 'text', value: '' },
      { key: 'hvac_ducts_issued', label: 'HVAC Ducts Issued', type: 'number', value: '' },
      { key: 'fire_safety_equipment', label: 'Fire Safety Equipment', type: 'text', value: '' },
      { key: 'hvac_units_received', label: 'HVAC Units Received', type: 'number', value: '' },
      { key: 'fire_safety_gear', label: 'Fire Safety Gear', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'enrollment_projection', label: 'Enrollment Projection', type: 'number', value: '' },
      { key: 'curriculum_planning', label: 'Curriculum Planning', type: 'text', value: '' },
      { key: 'classroom_adjustments', label: 'Classroom Adjustments', type: 'number', value: '' },
      { key: 'building_erection', label: 'Building Erection', type: 'percentage', value: '' },
      { key: 'student_safety_index', label: 'Student Safety Index', type: 'number', value: '' },
      { key: 'accreditation_status', label: 'Accreditation Status', type: 'text', value: '' },
      { key: 'staff_induction_schedule', label: 'Staff Induction Schedule', type: 'date', value: '' },
      { key: 'student_admission_start', label: 'Student Admission Start', type: 'date', value: '' },
      { key: 'lab_furniture_issued', label: 'Lab Furniture Issued', type: 'number', value: '' },
      { key: 'sports_gear', label: 'Sports Gear', type: 'text', value: '' },
      { key: 'lab_equipment_boxes', label: 'Lab Equipment Boxes', type: 'number', value: '' },
      { key: 'sports_equipment', label: 'Sports Equipment', type: 'text', value: '' },
    ],
    Water: [
      { key: 'water_demand', label: 'Water Demand', type: 'volume', value: '' },
      { key: 'pipeline_route', label: 'Pipeline Route', type: 'distance', value: '' },
      { key: 'flow_rate_adjustment', label: 'Flow Rate Adjustment', type: 'volume', value: '' },
      { key: 'pipe_installation', label: 'Pipe Installation', type: 'distance', value: '' },
      { key: 'flow_rate_consistency', label: 'Flow Rate Consistency', type: 'volume', value: '' },
      { key: 'water_quality_clearance', label: 'Water Quality Clearance', type: 'text', value: '' },
      { key: 'operational_sops', label: 'Operational SOPs', type: 'text', value: '' },
      { key: 'pipe_sections_issued', label: 'Pipe Sections Issued', type: 'number', value: '' },
      { key: 'valve_units', label: 'Valve Units', type: 'number', value: '' },
      { key: 'pipe_sections', label: 'Pipe Sections', type: 'number', value: '' },
      { key: 'chlorination_units', label: 'Chlorination Units', type: 'number', value: '' },
    ],
    Power: [
      { key: 'power_demand', label: 'Power Demand', type: 'power', value: '' },
      { key: 'substation_location', label: 'Substation Location', type: 'coordinates', value: '' },
      { key: 'load_balancing', label: 'Load Balancing', type: 'power', value: '' },
      { key: 'cable_laying', label: 'Cable Laying', type: 'distance', value: '' },
      { key: 'voltage_stability_logs', label: 'Voltage Stability Logs', type: 'text', value: '' },
      { key: 'grid_integration_status', label: 'Grid Integration Status', type: 'text', value: '' },
      { key: 'transformer_oil', label: 'Transformer Oil', type: 'volume', value: '' },
      { key: 'cable_rolls', label: 'Cable Rolls', type: 'number', value: '' },
      { key: 'cable_drums', label: 'Cable Drums', type: 'number', value: '' },
      { key: 'transformer_oil_drums', label: 'Transformer Oil Drums', type: 'volume', value: '' },
    ],
    Gas: [
      { key: 'gas_demand', label: 'Gas Demand', type: 'volume', value: '' },
      { key: 'pressure_requirements', label: 'Pressure Requirements', type: 'pressure', value: '' },
      { key: 'pressure_adjustment', label: 'Pressure Adjustment', type: 'pressure', value: '' },
      { key: 'valve_installation', label: 'Valve Installation', type: 'number', value: '' },
      { key: 'leak_detection_alerts', label: 'Leak Detection Alerts', type: 'number', value: '' },
      { key: 'safety_clearance', label: 'Safety Clearance', type: 'text', value: '' },
      { key: 'welding_rods', label: 'Welding Rods', type: 'number', value: '' },
      { key: 'pressure_gauges', label: 'Pressure Gauges', type: 'number', value: '' },
      { key: 'pressure_regulators', label: 'Pressure Regulators', type: 'number', value: '' },
      { key: 'safety_valves', label: 'Safety Valves', type: 'number', value: '' },
    ],
    Telecom: [
      { key: 'coverage_estimate', label: 'Coverage Estimate', type: 'area', value: '' },
      { key: 'tower_locations', label: 'Tower Locations', type: 'number', value: '' },
      { key: 'bandwidth_upgrade', label: 'Bandwidth Upgrade', type: 'bandwidth', value: '' },
      { key: 'antenna_setup', label: 'Antenna Setup', type: 'number', value: '' },
      { key: 'signal_strength_logs', label: 'Signal Strength Logs', type: 'text', value: '' },
      { key: 'equipment_health_index', label: 'Equipment Health Index', type: 'percentage', value: '' },
      { key: 'network_activation_date', label: 'Network Activation Date', type: 'date', value: '' },
      { key: 'sla_documentation', label: 'SLA Documentation', type: 'text', value: '' },
      { key: 'customer_onboarding_kit', label: 'Customer Onboarding Kit', type: 'text', value: '' },
      { key: 'fiber_cable_spools', label: 'Fiber Cable Spools', type: 'number', value: '' },
      { key: 'tower_bolts', label: 'Tower Bolts', type: 'number', value: '' },
      { key: 'antenna_units', label: 'Antenna Units', type: 'number', value: '' },
    ],
    Road: [
      { key: 'traffic_volume', label: 'Traffic Volume', type: 'number', value: '' },
      { key: 'lane_configuration', label: 'Lane Configuration', type: 'number', value: '' },
      { key: 'lane_width_adjustment', label: 'Lane Width Adjustment', type: 'distance', value: '' },
      { key: 'road_length', label: 'Road length', type: 'distance', value: '' },
      { key: 'pavement_layering', label: 'Pavement Layering', type: 'text', value: '' },
      { key: 'surface_condition_index', label: 'Surface Condition Index', type: 'number', value: '' },
      { key: 'road_safety_audit', label: 'Road Safety Audit', type: 'text', value: '' },
      { key: 'traffic_signage_installed', label: 'Traffic Signage Installed', type: 'text', value: '' },
      { key: 'public_access_date', label: 'Public Access Date', type: 'date', value: '' },
      { key: 'asphalt_quantity', label: 'Asphalt Quantity', type: 'weight', value: '' },
      { key: 'road_marking_paint', label: 'Road Marking Paint', type: 'volume', value: '' },
      { key: 'asphalt_delivered', label: 'Asphalt Delivered', type: 'weight', value: '' },
      { key: 'road_signage_boards', label: 'Road Signage Boards', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'consultancy_scope', label: 'Consultancy Scope', type: 'text', value: '' },
      { key: 'resource_allocation', label: 'Resource Allocation', type: 'text', value: '' },
      { key: 'scope_change_request', label: 'Scope Change Request', type: 'text', value: '' },
      { key: 'oversight_report', label: 'Oversight Report', type: 'text', value: '' },
      { key: 'monitoring_review_notes', label: 'Monitoring Review Notes', type: 'text', value: '' },
      { key: 'client_satisfaction_score', label: 'Client Satisfaction Score', type: 'percentage', value: '' },
      { key: 'risk_mitigation_updates', label: 'Risk Mitigation Updates', type: 'text', value: '' },
      { key: 'final_advisory_report', label: 'Final Advisory Report', type: 'text', value: '' },
      { key: 'client_feedback_summary', label: 'Client Feedback Summary', type: 'text', value: '' },
      { key: 'knowledge_transfer_docs', label: 'Knowledge Transfer Docs', type: 'text', value: '' },
      { key: 'stationery_issued', label: 'Stationery Issued', type: 'text', value: '' },
      { key: 'field_survey_kits', label: 'Field Survey Kits', type: 'number', value: '' },
      { key: 'survey_equipment', label: 'Survey Equipment', type: 'text', value: '' },
      { key: 'reference_materials', label: 'Reference Materials', type: 'text', value: '' },
    ]
  };

  permitStatusOptions = ['Approved', 'Pending'];
  userD: any;
  organizationId: any;
  projectId: any;
  branchList: any;
  branchShow = false;
  allState: any
  allDistricts: any
  allSubDistricts: any
  allVillage: any;
  availableStates: any[] = [];
  availableCities: any[] = [];
  stages: any[] = [{id: 1, name: 'Pre-Planning'}, {id: 2, name: 'Planning'}, {id: 3, name: 'Survey'}, {id: 4, name: 'Design Revisions & Scope Adjustments'}, {id: 5, name: 'Execution'}, {id: 6, name: 'Closure'}];
stageDocsList = ["feasibility","sanction","completion"];
  stageApprovals: { [key: string]: { key: string, label: string, type: string, value: string }[] } = {
    feasibility: [
      { key: 'ownership_certificate', label: 'Ownership Certificate/Extract', type: 'file', value: 'Ownership Certificate/Extract' },
      { key: 'non_encumbrance_certificate', label: 'Non-Encumbrance Certificate (NEC)', type: 'file', value: 'Non-Encumbrance Certificate (NEC)' },
      { key: 'land_use_change', label: 'Change in Land Use (CLU)/Non-Agriculture (NA) Permission', type: 'text', value: 'Change in Land Use (CLU)/Non-Agriculture (NA) Permission' },
      { key: 'airport_clearance', label: 'Airport Clearance', type: 'file', value: 'Airport Clearance' },
      { key: 'crz_clearance', label: 'Coastal Regulatory Zone (CRZ) Clearance', type: 'file', value: 'Coastal Regulatory Zone (CRZ) Clearance' }
    ],
    sanction: [
      { key: 'layout_plan_approval', label: 'Layout & Building Plan Approval', type: 'file', value: 'Layout & Building Plan Approval' },
      { key: 'construction_license', label: 'Development/Construction License', type: 'file', value: 'Development/Construction License' },
      { key: 'zoning_plan_approval', label: 'Demarcation/Zoning Plan Approval', type: 'file', value: 'Demarcation/Zoning Plan Approval' },
      { key: 'environment_clearance', label: 'Environment Clearance', type: 'file', value: 'Environment Clearance' },
      { key: 'tree_noc', label: 'NOC for Tree Cutting', type: 'file', value: 'NOC for Tree Cutting' },
      { key: 'drainage_noc', label: 'NOC for Drainage & Sewerage', type: 'file', value: 'NOC for Drainage & Sewerage' },
      { key: 'electrical_scheme', label: 'Electrical Scheme Approval', type: 'file', value: 'Electrical Scheme Approval' },
      { key: 'traffic_noc', label: 'NOC for Traffic & Coordination', type: 'file', value: 'NOC for Traffic & Coordination' },
      { key: 'fire_scheme', label: 'Fire Fighting Scheme Approval', type: 'text', value: 'Fire Fighting Scheme Approval' },
      { key: 'monument_approval', label: 'Ancient Monument Approval', type: 'file', value: 'Ancient Monument Approval' },
      { key: 'consent_establish_operate', label: 'Consent to Establish & Operate', type: 'file', value: 'Consent to Establish & Operate' },
      { key: 'borewell_certificate', label: 'Borewell Registration Certificate', type: 'file', value: 'Borewell Registration Certificate' },
      { key: 'excavation_approval', label: 'Excavation/Mining Approval', type: 'file', value: 'Excavation/Mining Approval' },
      { key: 'infra_approval', label: 'Infrastructure Layout & Common Facilities Approval', type: 'file', value: 'Infrastructure Layout & Common Facilities Approval' },
      { key: 'road_access_approval', label: 'Road Access Plan Approval', type: 'file', value: 'Road Access Plan Approval' },
      { key: 'lift_approval', label: 'Lift/Escalator Installation Approval', type: 'file', value: 'Lift/Escalator Installation Approval' },
      { key: 'electrical_load_sanction', label: 'Sanction of Electrical Load & DG/Transformer Approval', type: 'file', value: 'Sanction of Electrical Load & DG/Transformer Approval' },
      { key: 'employer_registration', label: 'Registration of Principal Employer', type: 'file', value: 'Registration of Principal Employer' },
      { key: 'welfare_board_registration', label: 'Registration with Construction Workers Welfare Board', type: 'file', value: 'Registration with Construction Workers Welfare Board' },
      { key: 'site_office_approval', label: 'Site Office Approval', type: 'file', value: 'Site Office Approval' },
      { key: 'hoarding_approval', label: 'Hoarding Approval', type: 'file', value: 'Hoarding Approval' },
      { key: 'iod_certificate', label: 'Intimation of Disapproval (IoD)/Commencement Certificate', type: 'file', value: 'Intimation of Disapproval (IoD)/Commencement Certificate' }
    ],
    completion: [
      { key: 'completion_certificate', label: 'Completion Certificate (CC)', type: 'file', value: 'Completion Certificate (CC)' },
      { key: 'permanent_power_connection', label: 'Permanent Power Connection', type: 'file', value: 'Permanent Power Connection' },
      { key: 'permanent_water_sewage', label: 'Permanent Water and Sewerage Connection', type: 'file', value: 'Permanent Water and Sewerage Connection' },
      { key: 'rwa_registration', label: 'Registration of RWA', type: 'file', value: 'Registration of RWA' },
      { key: 'occupancy_certificate', label: 'Occupancy Certificate (OC)', type: 'file', value: 'Occupancy Certificate (OC)' }
    ]
  };

  domainSanctionFields: { [key: string]: { key: string, label: string, type: string, value: string }[] } = {
    Commercial: [
      { key: 'commercial_zoning_approval', label: 'Commercial Zoning Approval', type: 'file', value: '' },
      { key: 'signage_approval', label: 'Signage Approval', type: 'file', value: '' },
      { key: 'enhanced_fire_safety_standards_clearance', label: 'Enhanced Fire Safety Standards Clearance', type: 'file', value: '' },
      { key: 'parking_requirements_approval', label: 'Parking Requirements Approval', type: 'file', value: '' },
      { key: 'accessibility_standards_compliance', label: 'Accessibility Standards Compliance', type: 'file', value: '' },
      { key: 'commercial_zoning_approval_office', label: 'Commercial Zoning Approval', type: 'file', value: '' },
      { key: 'fire_safety_approval_multi_storey', label: 'Enhanced Fire Safety Approval for multi-storey setups', type: 'file', value: '' },
      { key: 'accessibility_compliance_office', label: 'Accessibility Compliance', type: 'file', value: '' },
      { key: 'parking_plan_approval', label: 'Parking Plan Approval', type: 'file', value: '' },
      { key: 'electrical_load_approval', label: 'Electrical Load Approval for office-specific infrastructure', type: 'file', value: '' }
    ],
    Educational: [
      { key: 'educational_board_standards_compliance', label: 'Educational Board Standards Compliance (e.g. class size, labs, playgrounds)', type: 'file', value: '' },
      { key: 'noc_department_of_education', label: 'NOC from Department of Education (DOE)', type: 'file', value: '' },
      { key: 'recognition_certificate_post_construction', label: 'Recognition Certificate after construction', type: 'file', value: '' },
      { key: 'affiliation_approval', label: 'Affiliation Approval (e.g. CBSE/ICSE)', type: 'file', value: '' }
    ],
    Religious: [
      { key: 'zoning_approval_assembly_use', label: 'Zoning Approval for Assembly Use', type: 'file', value: '' },
      { key: 'religious_authority_approval', label: 'Religious Authority Approval (if applicable)', type: 'file', value: '' },
      { key: 'assembly_building_codes_compliance', label: 'Compliance with Assembly Building Codes (e.g. fire safety)', type: 'file', value: '' },
      { key: 'local_permissions_religious_structures', label: 'Local Permissions for Religious Structures', type: 'file', value: '' }
    ],

  };
  pickerRefs: any;
  clients: any = [];

  // Unit selection per field (number in the input stays as entered; save path uses standardizeFieldValues())
  fieldUnits: { [key: string]: string } = {};

  // Cache for available units to avoid repeated calculations
  availableUnitsCache: { [key: string]: any[] } = {};
  
  // Component initialization flag
  isComponentReady = false;
  
  // Cached size in SQM for display
  sizeInSqm: number | null = null;

  filteredClients!: Observable<{
    client_id: number; client_first_name: string; client_last_name: string
  }[]>;



  constructor(
    private fb: FormBuilder,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private unitConversionService: UnitConversionService
  ) { }
  ngAfterViewInit() {
    this.fileInputRefs.forEach(ref => {
      const inputId = ref.nativeElement.id;
      this.fileInput[inputId] = ref.nativeElement;
    });
  }

  triggerFileInput(index: number): void {
    const input = this.fileInputRefs.toArray()[index];
    input?.nativeElement?.click();
  }

  ngOnInit(): void {
    const user = localStorage.getItem('user') as any;
    this.userD = JSON.parse(user);
    this.organizationId = this.userD.user.org_id;

    // Safely get domain options
    const domainString = this.userD.user?.domain;
    this.domainOptions = domainString && typeof domainString === 'string' 
      ? domainString.split(",").map((d: string) => d.trim()).filter((d: string) => d.length > 0)
      : [];
    
    // Generate FSI options from 0.5 to 4.0 with 0.5 increments (must match DB / mat-option values)
    this.fsiOptions = [];
    for (let i = 0.5; i <= 4.0; i += 0.5) {
      this.fsiOptions.push(parseFloat(i.toFixed(1)));
    }
    
    this.getClientsData();
    this.fetchBranchList();
    this.getstates()
    this.route.params.subscribe((params) => {
      this.projectId = params['id'];

    });
    this.projectForm = this.fb.group({
      project_name: ['', Validators.required],
      project_id: [''],
      company_name: [this.userD.user.org_name, Validators.required],
      company_code: [this.userD.user.company_code, Validators.required],
      company_id: [this.userD.user.org_id, Validators.required],
      branch_name: [''],
      branch_id: [''],
      branch_code: [''],
      domain: ['', Validators.required],
      sub_domain: [''],
      infrastructure_type: [''],
      road_type: [''], // Road type (same as sub_domain for road domain)
      road_services: [[]], // Array of selected services
      size: ['', Validators.required],
      client_name: [''],
      start_date: ['', ],
      end_date: ['', ],
      budget_amount: [''],
      funding_source: [''],
      scope_description: [''],
      timeline_start: ['', Validators.required],
      timeline_end: ['', Validators.required],
      permit_status: ['', Validators.required],
      stage: [""],
      stage_document: [''],
      fsi: [2], // Default FSI value for construction projects
    });
    
    // Mark component as ready after form initialization
    this.isComponentReady = true;
    
    // Subscribe to size changes to update SQM display
    this.projectForm.get('size')?.valueChanges.subscribe(() => {
      this.updateSizeInSqm();
    });
    this.locationForm = this.formBuilder.group({
      location: this.formBuilder.array([this.createLocationGroup()])
    });
    this.availableStates[0] = [];
    this.availableCities[0] = [];

    this.clientForm = this.formBuilder.group({
      client_name: ["", Validators.required],
      client_first_name: ["", Validators.required],
      client_last_name: ["", Validators.required],
      contact_person: ["", Validators.required],
      contact_number: ["", [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      khasra_no: [""],
      area: ["", Validators.required],
      state: ["", Validators.required],
      district: ["", Validators.required],
      sub_district: ["", Validators.required],
      villages: ["", Validators.required],
      client_id: [""],
      line_1: ["", Validators.required],
      line_2: ["", Validators.required],

    });

    if (this.projectId) {
      this.patchProjectData();
    }
    this.filteredClients = this.clientForm.get('client_name')!.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  private _filter(value: string): { client_id: number; client_first_name: string; client_last_name: string }[] {
    const filterValue = value.toLowerCase();
    // console.log(this.clients);
    return this.clients.filter((client: any) =>
      client.client_first_name.toLowerCase().includes(filterValue)
    );
  }

  onClientSelected(event: any) {
    // console.log(event.option.value);
    // console.log(this.clients);
    const selectedClient = this.clients.find(
      (c: any) => c.client_first_name + ' ' + c.client_last_name === event.option.value
    );
    // console.log(selectedClient)
    if (selectedClient) {
      this.projectForm.patchValue({ client_id: selectedClient.id });
      this.clientForm.patchValue(selectedClient);

      this.getdistricts()
      this.getsubdistricts()
      this.getvillages()
    }
  }
  get location() {
    return this.locationForm.get('location') as FormArray;
  }

  createLocationGroup(): FormGroup {
    return this.formBuilder.group({
      lat: ['', Validators.required],
      long: ['', Validators.required,],
      city: ['', Validators.required,],
      state: ['', Validators.required],
      country: ['', Validators.required],
      pincode: ['', Validators.required],
      address_line_1: ["", Validators.required],
      address_line_2: ["", Validators.required],
      khasra_no: [""],
      area: [""],
    });
  }

  addBranch() {
    this.location.push(this.createLocationGroup());
    const index = this.location.length - 1;
    this.availableStates[index] = [];
    this.availableCities[index] = [];
  }

  removeBranch(index: number) {
    if (this.location.length > 1) {
      this.location.removeAt(index);
    }
    this.availableStates.splice(index, 1);
    this.availableCities.splice(index, 1);
  }
  onDomainChange(domain: string) {
    this.selectedDomain = domain;
    console.log(domain);
    
    // Size field is hidden for Road & Utilities - make it optional; otherwise required
    const sizeControl = this.projectForm.get('size');
    if (sizeControl) {
      const domainLower = (domain || '').toLowerCase();
      if (domainLower === 'road' || domainLower === 'utilities') {
        sizeControl.clearValidators();
        sizeControl.setValue(sizeControl.value || 0); // default when hidden
      } else {
        sizeControl.setValidators(Validators.required);
      }
      sizeControl.updateValueAndValidity();
    }

    // Get current sub_domain value before resetting
    const currentSubDomain = this.projectForm.get('sub_domain')?.value;
    const availableSubDomains = this.getSubDomains();
    
    // Only reset sub_domain if:
    // 1. Not in edit mode, OR
    // 2. Current sub_domain is not valid for the new domain
    const shouldResetSubDomain = !this.editMode || 
                                  !currentSubDomain || 
                                  !availableSubDomains.includes(currentSubDomain);
    
    if (shouldResetSubDomain) {
      this.projectForm.patchValue({ sub_domain: '' });
    }
    
    Object.keys(this.projectForm.controls).forEach(key => {
      if (key !== 'domain' && key !== 'sub_domain' && this.domainFields[domain]?.find(field => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some(field => field.key === key)) {
        this.projectForm.removeControl(key);
      }
    });
    console.log(this.domainFields[domain]);
    // if (this.editMode) {
      this.domainFields[domain]?.forEach(field => {
        this.projectForm.addControl(field.key, this.fb.control(field.value));
      });
      // this.onDomChange(domain);
    // }
  }
  onDocChange(stage_document: string) {
    this.selectedDoc = stage_document;
    console.log(stage_document);

    Object.keys(this.projectForm.controls).forEach(key => {
      if (key !== 'stage_document' && this.stageApprovals[stage_document]?.find(field => field.key === key) === undefined &&
        Object.values(this.stageApprovals).flat().some(field => field.key === key)) {
        this.projectForm.removeControl(key);
      }
    });
    console.log("2", this.stageDocsList[stage_document as any]);
    this.stageApprovals[this.stageDocsList[stage_document as any]].forEach(field => {
      this.projectForm.addControl(field.key, this.fb.control(field.value));
    });
  }
  onDomChange(domain_document: string) {
    this.selectedDom = domain_document;
    Object.keys(this.projectForm.controls).forEach(key => {
      if (key !== 'stage_document' && this.domainSanctionFields[domain_document]?.find(field => field.key === key) === undefined &&
        Object.values(this.domainSanctionFields).flat().some(field => field.key === key)) {
        this.projectForm.removeControl(key);
      }
    });
    this.domainSanctionFields[domain_document].forEach(field => {
      this.projectForm.addControl(field.key, this.fb.control(field.value));
    });
  }
  fetchBranchList() {
    this.apiService.getOrganizationsBranch(this.organizationId).subscribe({
      next: (res: any) => {
        // console.log(res);
        if (res?.data?.length != 0) {
          this.branchList = res?.data;;
          this.branchShow = true
        }
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.message);
      },
      complete: () => '',
    });
  }


  patchProjectData() {
    this.apiService.getProjectCommonId(this.projectId).subscribe({
      next: (res: any) => {

        this.editMode = true;
        this.projectName = res.project_name;
        this.projectId = res.id;

        this.projectForm.patchValue({
          project_name: res.project_name,
          project_id: res.id,
          company_name: this.userD.user.org_name,
          company_id: this.userD.user.org_id,
          company_code: this.userD.user.company_code,
          branch_id: +res.branch_id,
          branch_name: res.branch_name,
          domain: res.domain,
          sub_domain: res.sub_domain || '',
          infrastructure_type: res.infrastructure_type,
          size: res.size,
          client_name: res.client_name,
          start_date: res.start_date,
          end_date: res.end_date,
          budget_amount: res.budget_amount,
          funding_source: res.funding_source,
          scope_description: res.scope_description,
          timeline_start: res.timeline_start ? new Date(res.timeline_start) : '',
          timeline_end: res.timeline_end ? new Date(res.timeline_end) : '',
          permit_status: res.permit_status,
          stage: res.stage,
          fsi: this.normalizeFsiForSelect(res.fsi, res.sub_domain),
        });

        // Set size unit if available
        if (res.size_unit) {
          this.fieldUnits['size'] = res.size_unit;
        } else if (res.size) {
          // Default to sqm if size exists but no unit
          this.fieldUnits['size'] = 'sqm';
        }
        
        // Update size in SQM display after data is loaded
        setTimeout(() => {
          this.updateSizeInSqm();
        }, 100);
        
        // Call onDomainChange first to set up domain fields
        this.onDomainChange(this.projectForm.value.domain);
        
        // After domain change, restore sub_domain if it exists and is valid for the domain
        setTimeout(() => {
          if (res.sub_domain) {
            const availableSubDomains = this.getSubDomains();
            if (availableSubDomains.includes(res.sub_domain)) {
              this.projectForm.patchValue({ sub_domain: res.sub_domain });
            }
          }
          // Re-apply FSI after domain/sub_domain hooks so mat-select always shows a valid option
          this.projectForm.patchValue({
            fsi: this.normalizeFsiForSelect(res.fsi, res.sub_domain),
          });
          
          // Load road_type and road_services for road domain (API returns at top level; fallback to metadata)
          if (res.domain && res.domain.toLowerCase() === 'road') {
            try {
              const metadata = res.metadata ? (typeof res.metadata === 'string' ? JSON.parse(res.metadata) : res.metadata) : {};
              if (metadata.road_type || res.road_type) {
                this.projectForm.patchValue({ 
                  road_type: res.road_type || metadata.road_type,
                  sub_domain: res.sub_domain || metadata.road_type || res.road_type
                });
              }
              const roadServices = (res.road_services && Array.isArray(res.road_services))
                ? res.road_services
                : (metadata.road_services && Array.isArray(metadata.road_services))
                  ? metadata.road_services
                  : [];
              if (roadServices.length > 0) {
                this.projectForm.patchValue({ road_services: roadServices });
              }
            } catch (e) {
              console.error('Error parsing metadata for road project:', e);
            }
          }
        }, 50);

        if (this.editMode) {
          this.domainFields[this.projectForm.value.domain as any]?.forEach(field => {
            if (res.hasOwnProperty(field.key)) {
              this.projectForm.get(field.key)?.patchValue(res[field.key]);
            }
          });
        }
        console.log(this.domainFields)
        const lData = res?.location || [];
        const branchFormArray = this.location;
        branchFormArray.clear();

        lData.forEach((loc: {
          address_line_2: string;
          address_line_1: string; country: any; state: any; city: any; lat: any; long: any; pincode: any; officename: any; khasra_no: any; area: any;
        }, index: number) => {
          const branchGroup = this.createLocationGroup();
          this.location.push(branchGroup);

          branchGroup.patchValue({ country: loc.country || '' });
          this.onCountryBChange(index);

          setTimeout(() => {
            // console.log(index, loc);
            branchGroup.patchValue({ state: loc.state || '' });
            this.onStateBChange(index);


            branchGroup.patchValue({
              city: loc.city || '',
              lat: loc.lat || '',
              long: loc.long || '',
              pincode: loc.pincode || '',
              officename: loc.officename || '',
              address_line_1: loc.address_line_1 || '',
              address_line_2: loc.address_line_2 || '',
              khasra_no: loc?.khasra_no || '',
              area: loc?.area || '',
            });
          }, 200);
        });


        if (branchFormArray.length === 0) {
          branchFormArray.push(this.createLocationGroup());
        }



        const client = res
        this.clientForm.patchValue({
          client_name: client.client_first_name,
          client_last_name: client.client_last_name,
          client_first_name: client.client_first_name,
          contact_number: client.contact_number,
          state: client.state,
          district: client.district,
          sub_district: client.sub_district,
          villages: client.villages,
          khasra_no: client.khasra_no,
          area: client.area,
          client_id: client.client_id,
          line_1: client.line_1,
          line_2: client.line_2


        });
        this.getdistricts()
        this.getsubdistricts()
        this.getvillages()
        // console.log(this.clientForm.value)


      },
      error: (err) => {

        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }

  openPicker(index: number, branch: any) {
    const dialogRef = this.dialog.open(PlacePickerComponent, {
      width: '600px',
      data: { city: this.locationForm.value.location[index].city, state: this.locationForm.value.location[index].state, country: this.locationForm.value.location[index].country }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        branch.patchValue({
          lat: result.lat,
          long: result.lng
        });
      }
    });
  }

  onCountryBChange(index: number) {
    const branch = this.location.at(index);
    const selectedCountry = branch.get('country')?.value;
    this.apiService.getstates().subscribe({
      next: (res) => {
        this.availableStates[index] = res ? res : []
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
    branch.patchValue({ state: '', city: '' });
  }

  onStateBChange(index: number) {
    const branch = this.location.at(index);
    const selectedCountry = branch.get('country')?.value;
    const selectedState = branch.get('state')?.value;
    this.apiService.getdistricts(selectedState.toUpperCase()).subscribe({
      next: (res) => {
        this.availableCities[index] = res ? res : [];
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
    branch.patchValue({ city: '' });
  }


  getstates() {
    this.apiService.getstates().subscribe({
      next: (res) => {
        this.allState = res
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }

  getClientsData() {
    this.apiService.getClients().subscribe({
      next: (res: any) => {
        // console.log(res);
        this.clients = res?.data || [];
        this.filteredClients = this.clientForm.get('client_name')!.valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value || ''))
        );
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }


  getdistricts() {
    const state = this.clientForm.value.state;
    this.apiService.getdistricts(state.toUpperCase()).subscribe({
      next: (res) => {
        this.allDistricts = res
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }
  getsubdistricts() {
    const state = this.clientForm.value.state;
    const district = this.clientForm.value.district;
    this.apiService.getSubdistricts(state.toUpperCase(), district .toUpperCase()).subscribe({
      next: (res) => {
        this.allSubDistricts = res
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }

  getvillages() {
    const state = this.clientForm.value.state;
    const district = this.clientForm.value.district;
    const subdistrict = this.clientForm.value.sub_district;
    this.apiService.getvillages(state, district, subdistrict).subscribe({
      next: (res) => {
        this.allVillage = res
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => '',
    });
  }
  onBranchChange(selectedBranchId: any) {
    // console.log(selectedBranchId)
    const selectedBranch = this.branchList.find((b: { branch_id: any; }) => b.branch_id === selectedBranchId);
    // console.log(selectedBranch)
    const manager = selectedBranch?.primary_contact_name || '';
    this.projectForm.get('branch_name')?.setValue(manager);
  }
  /**
   * Convert field value to standard unit before saving
   */
  async standardizeFieldValues(formData: any): Promise<any> {
    const standardizedData = { ...formData };
    const domain = this.projectForm.get('domain')?.value;
    const domainFields = this.domainFields[domain] || [];

    // Process size field (always has unit conversion)
    if (formData.size && this.fieldUnits['size']) {
      try {
        const result = await this.apiService.standardizeProjectField({
          value: parseFloat(formData.size),
          fieldType: 'size',
          unit: this.fieldUnits['size']
        }).toPromise();

        if (result?.success && result.data.standardized) {
          standardizedData.size = result.data.value;
          standardizedData.size_unit = result.data.unit;
          standardizedData.size_original_unit = result.data.originalUnit;
        } else {
          // Fallback: if standardization fails, use the original value but log a warning
          console.warn('Size standardization failed, using original value:', formData.size);
          standardizedData.size = parseFloat(formData.size) || 0;
          standardizedData.size_unit = this.fieldUnits['size'] || 'sqm';
        }
      } catch (error) {
        console.warn('Failed to standardize size unit:', error);
        // Fallback: use original value if API call fails
        standardizedData.size = parseFloat(formData.size) || 0;
        standardizedData.size_unit = this.fieldUnits['size'] || 'sqm';
      }
    } else if (formData.size) {
      // If size exists but no unit is set, use the value as-is and default to sqm
      standardizedData.size = parseFloat(formData.size) || 0;
      standardizedData.size_unit = 'sqm';
    }

    // Process each domain field that has a unit
    for (const field of domainFields) {
      const fieldKey = field.key;
      const fieldType = field.type;
      const fieldValue = formData[fieldKey];
      const fieldUnit = this.fieldUnits[fieldKey];

      // Skip if no value or unit or not a convertible type
      if (!fieldValue || !fieldUnit || 
          fieldType === 'text' || fieldType === 'date' || 
          fieldType === 'number' || fieldType === 'percentage') {
        continue;
      }

      // Standardize unit for storage
      try {
        const result = await this.apiService.standardizeProjectField({
          value: parseFloat(fieldValue),
          fieldType: fieldType,
          unit: fieldUnit
        }).toPromise();

        if (result?.success && result.data.standardized) {
          standardizedData[fieldKey] = result.data.value;
          // Store unit info in metadata
          standardizedData[`${fieldKey}_unit`] = result.data.unit;
          standardizedData[`${fieldKey}_original_unit`] = result.data.originalUnit;
        }
      } catch (error) {
        console.warn(`Failed to standardize unit for ${fieldKey}:`, error);
        // Continue with original value if conversion fails
      }
    }

    return standardizedData;
  }

  /**
   * Update size in SQM for display
   */
  updateSizeInSqm() {
    if (!this.isComponentReady || !this.projectForm || !this.unitConversionService) {
      this.sizeInSqm = null;
      return;
    }

    const sizeValue = this.projectForm.get('size')?.value;
    if (!sizeValue || isNaN(parseFloat(sizeValue))) {
      this.sizeInSqm = null;
      return;
    }

    const currentUnit = this.getFieldUnit('size');
    const numericValue = parseFloat(sizeValue);
    
    // If already in sqm or m2, use as is
    if (currentUnit === 'sqm' || currentUnit === 'm2') {
      this.sizeInSqm = numericValue;
      return;
    }

    // Convert to sqm using the service
    try {
      const fromUnit = this.unitConversionService.units.find(u => u.code.toLowerCase() === currentUnit.toLowerCase());
      const toUnit = this.unitConversionService.units.find(u => u.code.toLowerCase() === 'sqm');
      
      if (fromUnit && toUnit && fromUnit.category === 'area' && toUnit.category === 'area') {
        const converted = this.unitConversionService.convert(numericValue, currentUnit, 'sqm');
        this.sizeInSqm = converted !== null ? converted : numericValue;
      } else {
        // Fallback: try API conversion
        this.apiService.convertProjectField({
          value: numericValue,
          fieldType: 'size',
          fromUnit: currentUnit,
          toUnit: 'sqm'
        }).subscribe({
          next: (res: any) => {
            if (res.success && res.data.converted) {
              this.sizeInSqm = res.data.value;
            } else {
              this.sizeInSqm = numericValue;
            }
          },
          error: (err: any) => {
            console.warn('Error converting size to sqm:', err);
            this.sizeInSqm = numericValue;
          }
        });
      }
    } catch (error) {
      console.warn('Error converting size to sqm:', error);
      this.sizeInSqm = null;
    }
  }

  /**
   * Get size in SQM for display (readonly)
   */
  getSizeInSqm(): number | null {
    return this.sizeInSqm * this.projectForm.get('fsi')?.value;
  }

  /**
   * Get available units for a field (with caching and null safety)
   */
  getAvailableUnitsForField(fieldType: string): any[] {
    if (!fieldType || !this.isComponentReady) {
      return [];
    }
    
    // Check if service is available
    if (!this.unitConversionService) {
      return [];
    }
    
    // Check cache first
    if (this.availableUnitsCache[fieldType]) {
      return this.availableUnitsCache[fieldType];
    }
    
    // Get units from service and cache
    try {
      const units = this.unitConversionService.getUnitsForFieldType(fieldType);
      if (units && Array.isArray(units) && units.length > 0) {
        this.availableUnitsCache[fieldType] = units.map(u => ({ code: u.code, name: u.name }));
        return this.availableUnitsCache[fieldType];
      }
    } catch (error) {
      console.error('Error getting available units:', error);
    }
    
    return [];
  }

  /**
   * Get current unit for a field (with safe fallback, optimized for template)
   */
  getFieldUnit(fieldKey: string): string {
    if (!fieldKey || !this.isComponentReady) {
      return fieldKey === 'size' ? 'sqm' : 'nos';
    }
    
    // Return cached unit if available
    if (this.fieldUnits[fieldKey]) {
      return this.fieldUnits[fieldKey];
    }
    
    // Get default unit and cache it (only if form is ready)
    try {
      if (this.projectForm) {
        const defaultUnit = this.getDefaultUnitForField(fieldKey);
        if (defaultUnit && defaultUnit !== 'nos') {
          this.fieldUnits[fieldKey] = defaultUnit;
          return defaultUnit;
        }
      }
    } catch (error) {
      // Silently fail during initialization
    }
    
    // Return safe default
    return fieldKey === 'size' ? 'sqm' : 'nos';
  }

  /**
   * Set unit for a field
   */
  setFieldUnit(fieldKey: string, unit: string) {
    this.fieldUnits[fieldKey] = unit;
  }

  /**
   * Get default unit for a field type (safe, no form access)
   */
  getDefaultUnitForField(fieldKey: string): string {
    // Special case for size field
    if (fieldKey === 'size') {
      return 'sqm';
    }
    
    // Try to get from domain fields if form is available
    try {
      if (this.projectForm) {
        const domain = this.projectForm.get('domain')?.value;
        if (domain && this.domainFields[domain]) {
          const domainFields = this.domainFields[domain] || [];
          const field = domainFields.find((f: any) => f.key === fieldKey);
          
          if (field) {
            const defaultUnits: { [key: string]: string } = {
              'distance': 'm',
              'size': 'sqm',
              'area': 'sqm',
              'volume': 'cum',
              'weight': 'kg',
              'duration': 'hr',
              'number': 'nos',
              'percentage': 'percentage'
            };
            
            return defaultUnits[field.type] || 'nos';
          }
        }
      }
    } catch (error) {
      // Form might not be initialized yet - use safe defaults
    }
    
    return 'nos';
  }

  /**
   * Handle size unit change — updates unit only; does not alter the numeric size input.
   */
  onSizeUnitChange(newUnit: string) {
    if (!newUnit) return;
    this.setFieldUnit('size', newUnit);
    this.updateSizeInSqm();
  }

  /**
   * Handle sub-domain change - update road_type for road domain
   */
  onSubDomainChange() {
    const domain = this.projectForm?.get('domain')?.value?.toLowerCase();
    const subDomain = this.projectForm?.get('sub_domain')?.value;
    
    if (domain === 'road' && subDomain) {
      // Update road_type when sub_domain changes for road domain
      this.projectForm.patchValue({ road_type: subDomain });
      // Clear services when road type changes
      this.projectForm.patchValue({ road_services: [] });
    }

    // Map default FSI from sub-domain on new projects (construction-style domains with size/FSI)
    if (this.showSizeAndFsiFields() && subDomain && !this.editMode) {
      this.projectForm.patchValue({
        fsi: this.normalizeFsiForSelect(null, subDomain),
      });
    }
  }

  /**
   * Handle service checkbox change
   */
  onServiceChange(event: any, service: string) {
    const currentServices = this.projectForm.get('road_services')?.value || [];
    let updatedServices: string[];

    if (event.checked) {
      // Add service if checked
      updatedServices = [...currentServices, service];
    } else {
      // Remove service if unchecked
      updatedServices = currentServices.filter((s: string) => s !== service);
    }

    this.projectForm.patchValue({ road_services: updatedServices });
  }

  /**
   * Check if a service is selected
   */
  isServiceSelected(service: string): boolean {
    const services = this.projectForm.get('road_services')?.value || [];
    return services.includes(service);
  }

  /**
   * Handle field unit change (for domain fields) — updates unit only; does not alter the numeric value.
   */
  onFieldUnitChange(fieldKey: string, fieldType: string, newUnit: string) {
    if (!fieldKey || !fieldType || !newUnit) return;
    this.setFieldUnit(fieldKey, newUnit);
    if (fieldKey === 'size') {
      this.updateSizeInSqm();
    }
  }

  private formatDateForSave(value: unknown): string | null {
    if (!value) return null;
    try {
      const d = value instanceof Date ? value : new Date(value as string);
      if (Number.isNaN(d.getTime())) return null;
      return formatDate(d, 'yyyy-MM-dd', 'en-US');
    } catch {
      return null;
    }
  }

  private prepareFormDataForSave(formData: any): any {
    const payload = { ...formData };
    for (const key of ['timeline_start', 'timeline_end', 'start_date', 'end_date']) {
      if (payload[key]) {
        payload[key] = this.formatDateForSave(payload[key]);
      }
    }
    return payload;
  }

  onSubmit(): void {
    if (this.projectForm.valid) {
      const formData = this.prepareFormDataForSave({
        ...this.projectForm.value,
        ...this.locationForm.value,
        ...this.clientForm.value
      });

      // Standardize units before saving
      this.standardizeFieldValues(formData).then(standardizedData => {
        if (this.projectId) {
          this.apiService.updateProjectCommon(this.projectId, standardizedData).subscribe({
            next: (res: any) => {
              this.toastrMsgService.success("Project updated successfully");
              if (res?.auto_boq?.boq_code) {
                this.toastrMsgService.success(
                  `BOQ ${res.auto_boq.boq_code} auto-generated from project scope`
                );
              }
              this.router.navigate(["/home/projects"]);
            },
            error: (err) => {
              this.toastrMsgService.error(err.error?.errorMessage || err.error?.error || "Failed to update project");
            }
          });
        } else {
          this.apiService.saveProjectCommon(standardizedData).subscribe({
            next: (res) => {
              this.toastrMsgService.success("Project saved successfully");
              this.router.navigate(["/home/projects"]);
            },
            error: (err) => {
              this.toastrMsgService.error(err.error?.errorMessage || err.error?.error || "Failed to save project");
            }
          });
        }
      }).catch(error => {
        console.error('Error standardizing units:', error);
        // Continue with original data if standardization fails
        const formData = this.prepareFormDataForSave({
          ...this.projectForm.value,
          ...this.locationForm.value,
          ...this.clientForm.value
        });
        
        if (this.projectId) {
          this.apiService.updateProjectCommon(this.projectId, formData).subscribe({
            next: (res: any) => {
              this.toastrMsgService.success("Project updated successfully");
              if (res?.auto_boq?.boq_code) {
                this.toastrMsgService.success(
                  `BOQ ${res.auto_boq.boq_code} auto-generated from project scope`
                );
              }
              this.router.navigate(["/home/projects"]);
            },
            error: (err) => {
              this.toastrMsgService.error(err.error?.errorMessage || err.error?.error || "Failed to update project");
            }
          });
        } else {
          this.apiService.saveProjectCommon(formData).subscribe({
            next: (res) => {
              this.toastrMsgService.success("Project saved successfully");
              this.router.navigate(["/home/projects"]);
            },
            error: (err) => {
              this.toastrMsgService.error(err.error?.errorMessage || err.error?.error || "Failed to save project");
            }
          });
        }
      });
    } else {
      this.projectForm.markAllAsTouched();
      this.toastrMsgService.error("Please fill all required fields");
    }
  }
  onFileChange(event: Event, fieldKey: string) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.projectForm.get(fieldKey)?.setValue(file); // or store in a separate object
    }
  }
}